import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Plus, Target, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { PipelineCard } from "./PipelineCard";
import { NewPipelineSheet } from "./NewPipelineSheet";
import { NewStageSheet } from "./NewStageSheet";
import { SortableStage, type Stage } from "./SortableStage";

export function PipelinesSection() {
  const pipelinesQuery = trpc.crm.pipelines.list.useQuery({ includeArchived: false });
  const ensureDefault = trpc.crm.pipelines.ensureDefault.useMutation({
    onSuccess: () => {
      pipelinesQuery.refetch();
      toast.success("Funil padrão criado");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!selectedId && (pipelinesQuery.data?.length ?? 0) > 0) {
      setSelectedId(pipelinesQuery.data![0].id);
    }
    if (selectedId && !(pipelinesQuery.data ?? []).find((p: any) => p.id === selectedId)) {
      setSelectedId(pipelinesQuery.data?.[0]?.id ?? null);
    }
  }, [pipelinesQuery.data, selectedId]);

  const stagesQuery = trpc.crm.stages.list.useQuery(
    selectedId ? { pipelineId: selectedId } : (undefined as any),
    { enabled: !!selectedId },
  );

  const utils = trpc.useUtils();
  const archivePipeline = trpc.crm.pipelines.archive.useMutation({
    onSuccess: () => {
      utils.crm.pipelines.list.invalidate();
      toast.success("Funil arquivado");
    },
  });
  const updateStage = trpc.crm.stages.update.useMutation({
    onSuccess: () => utils.crm.stages.list.invalidate({ pipelineId: selectedId! }),
  });
  const deleteStage = trpc.crm.stages.delete.useMutation({
    onSuccess: () => {
      utils.crm.stages.list.invalidate({ pipelineId: selectedId! });
      toast.success("Estágio excluído");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const reorder = trpc.crm.stages.reorder.useMutation({
    onSuccess: () => utils.crm.stages.list.invalidate({ pipelineId: selectedId! }),
  });

  const [newPipelineOpen, setNewPipelineOpen] = useState(false);
  const [newStageOpen, setNewStageOpen] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<any | null>(null);
  const [deleteStageConfirm, setDeleteStageConfirm] = useState<any | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const [localStages, setLocalStages] = useState<Stage[]>([]);
  useEffect(() => {
    setLocalStages((stagesQuery.data ?? []) as Stage[]);
  }, [stagesQuery.data]);

  const stagesCountByPipeline = useMemo(() => {
    // Não temos contagem de stages em cada pipeline no backend, então mostramos apenas do selecionado.
    const map: Record<number, number> = {};
    if (selectedId) map[selectedId] = localStages.length;
    return map;
  }, [selectedId, localStages]);

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id || !selectedId) return;
    const oldIdx = localStages.findIndex((s) => s.id === active.id);
    const newIdx = localStages.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(localStages, oldIdx, newIdx);
    setLocalStages(reordered);
    reorder.mutate({
      pipelineId: selectedId,
      orderedIds: reordered.map((s) => s.id),
    });
  }

  if (pipelinesQuery.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const pipelines = pipelinesQuery.data ?? [];
  const selected = pipelines.find((p: any) => p.id === selectedId);

  return (
    <>
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4" /> Funis e estágios
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Organize suas oportunidades em funis com estágios customizados.
            </p>
          </div>
          <div className="flex gap-2">
            {pipelines.length === 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => ensureDefault.mutate()}
                disabled={ensureDefault.isPending}
              >
                {ensureDefault.isPending && (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                )}
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Criar funil padrão
              </Button>
            )}
            <Button size="sm" onClick={() => setNewPipelineOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> Novo funil
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {pipelines.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed rounded-lg">
              <Target className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <h3 className="font-semibold mb-1">Nenhum funil ainda</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Crie o funil padrão para começar com Prospecção → Qualificação → Proposta
                → Negociação → Fechado/Perdido.
              </p>
              <Button
                onClick={() => ensureDefault.mutate()}
                disabled={ensureDefault.isPending}
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                Criar funil padrão
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
              {/* ── Lista de funis ───────────── */}
              <div className="space-y-2">
                {pipelines.map((p: any) => (
                  <PipelineCard
                    key={p.id}
                    pipeline={p}
                    active={selectedId === p.id}
                    stagesCount={stagesCountByPipeline[p.id] ?? 0}
                    onClick={() => setSelectedId(p.id)}
                    onArchive={() => setArchiveConfirm(p)}
                  />
                ))}
              </div>

              {/* ── Editor de estágios ────────── */}
              {selected && (
                <div className="min-w-0 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm">
                        Estágios de "{selected.name}"
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Arraste para reordenar. Editar o nome/cor salva automaticamente.
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setNewStageOpen(true)}>
                      <Plus className="h-4 w-4 mr-1" /> Estágio
                    </Button>
                  </div>

                  {stagesQuery.isLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-12" />
                      <Skeleton className="h-12" />
                      <Skeleton className="h-12" />
                    </div>
                  ) : localStages.length === 0 ? (
                    <div className="rounded-lg border-2 border-dashed p-6 text-center">
                      <p className="text-sm text-muted-foreground mb-2">
                        Nenhum estágio. Adicione o primeiro.
                      </p>
                      <Button size="sm" onClick={() => setNewStageOpen(true)}>
                        <Plus className="h-4 w-4 mr-1" /> Adicionar estágio
                      </Button>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={localStages.map((s) => s.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-1.5">
                          {localStages.map((s) => (
                            <SortableStage
                              key={s.id}
                              stage={s}
                              onChange={(patch) => {
                                setLocalStages((prev) =>
                                  prev.map((x) => (x.id === s.id ? { ...x, ...patch } : x)),
                                );
                                updateStage.mutate({ id: s.id, ...patch });
                              }}
                              onDelete={() => setDeleteStageConfirm(s)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <NewPipelineSheet
        open={newPipelineOpen}
        onOpenChange={setNewPipelineOpen}
        onCreated={(id) => setSelectedId(id)}
      />
      {selectedId && (
        <NewStageSheet
          open={newStageOpen}
          onOpenChange={setNewStageOpen}
          pipelineId={selectedId}
          nextPosition={localStages.length}
        />
      )}

      {/* Confirm archive pipeline */}
      <AlertDialog open={!!archiveConfirm} onOpenChange={(v) => !v && setArchiveConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arquivar funil?</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveConfirm?.name}" deixará de aparecer para novas oportunidades.
              Os deals existentes continuam ativos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (archiveConfirm) archivePipeline.mutate({ id: archiveConfirm.id });
                setArchiveConfirm(null);
              }}
            >
              Arquivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete stage */}
      <AlertDialog
        open={!!deleteStageConfirm}
        onOpenChange={(v) => !v && setDeleteStageConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir estágio?</AlertDialogTitle>
            <AlertDialogDescription>
              Só funciona se não houver oportunidades no estágio "{deleteStageConfirm?.name}".
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteStageConfirm) deleteStage.mutate({ id: deleteStageConfirm.id });
                setDeleteStageConfirm(null);
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
